import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../components/layout/SiteChrome";
import { OtpModal } from "../components/ui/OtpModal";
import { sellStore } from "../store/sellStore";
import { useAuth } from "../store/authStore";
import { request } from "../services/api";
import { imageUrl } from "../services/config";

// Returns [id, ...all descendant node ids] so products assigned to a parent
// sub-category also appear on every deeper (last) category page below it.
function descendantIds(nodes: any[], id: string): string[] {
  const parentOf = (n: any) => (typeof n.parentId === "string" ? n.parentId : n.parentId?._id);
  const ids: string[] = [id];
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    nodes.forEach(n => {
      if (parentOf(n) === cur && ids.indexOf(n._id) === -1) {
        ids.push(n._id);
        stack.push(n._id);
      }
    });
  }
  return ids;
}

export default function BrandPage() {
  const { brandId = "" } = useParams<{ brandId: string }>();
  const { isLoggedIn, customer } = useAuth();
  const nav  = useNavigate();

  const [otpOpen, setOtpOpen] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [subBrands, setSubBrands] = useState<any[]>([]);
  const [allNodes, setAllNodes] = useState<any[]>([]);
  const [brand, setBrand] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setSubBrands([]);
    setProducts([]);

    // Fetch brand node info + full tree, then products assigned to this node or any deeper sub-category
    request('/nodes').then(data => {
      const nodes = data.nodes || []
      setAllNodes(nodes);
      const b = nodes.find((n: any) => n._id === brandId);
      setBrand(b);

      const ids = descendantIds(nodes, brandId);
      return request('/products').then(pData => {
        const all = pData.products || []
        const filtered = all.filter((p: any) => {
          const pNodeId = p.nodeId?._id || p.nodeId;
          return ids.includes(pNodeId);
        });
        setProducts(filtered);
      });
    }).catch(console.error).finally(() => setLoading(false));

    // Fetch child sub-categories (e.g. Samsung under Android)
    request(`/nodes?parentId=${brandId}`).then(data => {
      setSubBrands(data.nodes || []);
    }).catch(console.error);
  }, [brandId]);

  const sortedSubBrands = [...subBrands].sort((a: any, b: any) => {
    const aIds = descendantIds(allNodes, a._id);
    const bIds = descendantIds(allNodes, b._id);
    const aHas = products.some((p: any) => {
      const pNodeId = p.nodeId?._id || p.nodeId;
      return aIds.includes(pNodeId);
    });
    const bHas = products.some((p: any) => {
      const pNodeId = p.nodeId?._id || p.nodeId;
      return bIds.includes(pNodeId);
    });
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return 0;
  });

  if (!loading && !brand) { nav("/"); return null; }

  const selectModel = (p: any) => {
    sellStore.setDevice(brandId, p._id, p._id, p.algoId?._id || p.algoId, p.algoBasePrice || p.basePrice, p);
    if (isLoggedIn) {
      // Logged-in users are already verified; set verified state so the
      // questionnaire guard doesn't redirect back to home.
      sellStore.setVerified(true, customer?._id);
      nav("/questionnaire");
    } else {
      setOtpOpen(true);
    }
  };

  return (
    <div className="page-bg">
      <Header />
      <div style={{ maxWidth:720,margin:"0 auto",padding:"32px 16px 72px" }}>
        <button onClick={() => nav(-1)} style={{ background:"none",border:"none",color:"var(--text2)",fontSize:13,cursor:"pointer",marginBottom:24,display:"flex",alignItems:"center",gap:5,fontFamily:"var(--font-body)" }}>
          ← Back
        </button>

        <div style={{ display:"flex",alignItems:"center",gap:16,marginBottom:28 }}>
          <div style={{ 
            width:56,
            height:56,
            borderRadius:16,
            background:"var(--indigo-light)",
            border:"2px solid var(--border-bright)",
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            overflow: "hidden",
            padding: brand?.image?.url ? 6 : 0
          }}>
            {brand?.image?.url ? (
              <img 
                src={imageUrl(brand.image.url)} 
                alt="" 
                style={{ width: "100%", height: "100%", objectFit: "contain" }} 
              />
            ) : (
              <span style={{ fontSize: 26, color: "var(--brand)" }}>{brand?.icon || "📁"}</span>
            )}
          </div>
          <div>
            <h1 style={{ fontFamily:"var(--font-display)",fontWeight:800,fontSize:"clamp(20px,4vw,28px)",color:"var(--text)",letterSpacing:"-0.5px" }}>
              {brand?.name || "Loading..."} Models
            </h1>
            <p style={{ fontSize:13,color:"var(--text2)",marginTop:3 }}>Select your device to get an instant price</p>
          </div>
        </div>

        {subBrands.length > 0 && (
          <>
            <div style={{ fontSize:13,fontWeight:700,color:"var(--text2)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12 }}>Sub-Categories</div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:14,marginBottom:32 }}>
              {sortedSubBrands.map((b, i) => (
                <div key={b._id} onClick={() => nav(`/brand/${b._id}`)}
                  style={{ cursor:"pointer", display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:9,background:"var(--surface)",border:"1.5px solid var(--border)",borderRadius:14,padding:"22px 12px",transition:"all 0.22s",boxShadow:"var(--shadow-sm)" }}
                  onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "#1D9E75"; el.style.transform = "translateY(-3px)"; el.style.boxShadow = "var(--shadow-lg)"; }}
                  onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "var(--border)"; el.style.transform = ""; el.style.boxShadow = "var(--shadow-sm)"; }}>
                  <div style={{ width:46,height:46,borderRadius:11,background:"#1D9E7516",border:"1.5px solid #1D9E7528",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",padding:b.image?.url?6:0 }}>
                    {b.image?.url ? (
                      <img src={imageUrl(b.image.url)} alt="" style={{ width:"100%",height:"100%",objectFit:"contain" }} />
                    ) : (
                      <span style={{ fontSize:18,fontWeight:700,fontFamily:"var(--font-display)",color:"#1D9E75" }}>{b.icon}</span>
                    )}
                  </div>
                  <span style={{ fontSize:13,fontWeight:600,color:"var(--text)" }}>{b.name}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(min(100%, 190px),1fr))",gap:16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', gridColumn: '1/-1', padding: 60, color: 'var(--text2)' }}>
              <div style={{ fontSize: 40, marginBottom: 15, animation: 'pulse 1.5s infinite' }}>📱</div>
              <div style={{ fontWeight: 600 }}>Loading premium devices...</div>
            </div>
          ) : products.map(p => {
            const base = p.algoBasePrice && p.algoBasePrice > 0 ? p.algoBasePrice : p.basePrice;
            const min = Math.round(base * 0.7);
            const max = base;
            const thumb = p.images?.[0]?.url;
            const thumbSrc = thumb ? imageUrl(thumb) : null;

            return (
              <div key={p._id} onClick={() => selectModel(p)} className="product-card">
                {/* Media */}
                <div className="product-card__media">
                  <div className="product-card__badge">Verified</div>
                  {thumbSrc
                    ? <img src={thumbSrc} alt={p.name} className="product-card__img" />
                    : <div className="product-card__fallback">📱</div>}
                </div>

                {/* Body */}
                <div className="product-card__body">
                  <div className="product-card__name">{p.name}</div>
                  <div className="product-card__meta">
                    {Array.isArray(p.storage) && p.storage.length > 0 ? p.storage.join(' · ') : (p.sku || 'Verified Device')}
                  </div>

                  <div className="product-card__divider" />

                  <div className="product-card__price-row">
                    <span className="product-card__price-label">Get up to</span>
                    <span className="product-card__price">₹{max.toLocaleString("en-IN")}</span>
                  </div>

                  <div className="product-card__cta">
                    Sell Now <span className="product-card__cta-arrow">→</span>
                  </div>
                </div>
              </div>
            )
          })}
          {!loading && products.length === 0 && (
            <div style={{ gridColumn:'1/-1', textAlign:'center', background:'var(--surface)', border:'1.5px dashed var(--border)', borderRadius:20, padding:'64px 24px', boxShadow:'var(--shadow-sm)' }}>
              <div style={{ fontSize:44, marginBottom:14 }}>🚀</div>
              <div style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:22, color:'var(--text)', letterSpacing:'-0.3px' }}>Coming Soon</div>
              <p style={{ fontSize:13, color:'var(--text2)', marginTop:8, maxWidth:380, margin:'8px auto 0', lineHeight:1.6 }}>
                We're currently adding {brand?.name || 'these'} models. Sell prices for this brand will be live here very soon.
              </p>
            </div>
          )}
        </div>
      </div>

      <OtpModal open={otpOpen} onClose={() => setOtpOpen(false)} />
    </div>
  );
}
